// Adapter for the Office's authenticated WebSocket messages. It never invents a channel, UID, token or permission.
// `voice-agora` is a server-wide selection: enabled but unavailable means voice down, not a private SFU fallback.
import type { UID } from 'agora-rtc-sdk-ng';
import type { AgoraPageGrant } from './agora-page';
import type { AgoraMicUnavailable } from './agora-mic';

type Peers = { uid: UID; peerId: string }[];
type Current = { generation: number; version: number; epoch: string; leaseId: string; appId: string; channel: string;
  uid: number; expiresAt: number; pendingExpiry: number; peers: Peers; ready: boolean; renewal: number; stt: boolean };
interface Options {
  enterSfu: () => void;
  enterAgoraWait: () => void;
  unavailable: (reason: string) => void;
  sendRefresh: () => void;
  grant: (grant: AgoraPageGrant) => Promise<void>;
  renew: (leaseId: string, token: string, expiresAt: number) => Promise<void>;
  roster: (leaseId: string, version: number, uids: UID[], peers: Peers) => void;
}
const counter = (n: unknown): n is number => Number.isSafeInteger(n) && (n as number) >= 0;
const text = (s: unknown): s is string => typeof s === 'string' && s.length > 0 && s.length <= 2048 && s.trim() === s;
const uid = (n: unknown): n is number => Number.isInteger(n) && (n as number) > 0 && (n as number) <= 0xffff_ffff;

/** Only the currently authenticated Office socket may feed this object. Caller must reset it on socket close. */
export function createAgoraAuthorization(options: Options) {
  let enabled = false, socket = 0, generation = -1, lastChanged = -1, lastVersion = -1, self = '', current: Current | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null, blockedMic: AgoraMicUnavailable | null = null;
  const clear = () => { if (timer) clearTimeout(timer); timer = null; };
  const request = () => { if (enabled && !blockedMic) options.sendRefresh(); };
  function retry(ms = 5000) { clear(); if (enabled && !blockedMic) timer = setTimeout(() => { request(); retry(); }, Math.min(2_147_483_647, Math.max(250, ms))); }
  function wait() { current = null; options.enterAgoraWait(); }
  function down(reason = 'unavailable') {
    current = null;
    if (reason === 'mic-denied' || reason === 'mic-missing') { blockedMic = reason; clear(); }
    options.unavailable(blockedMic ?? reason);
    if (!blockedMic) retry();
  }
  function peersOf(value: unknown, ownUid: number): Peers | null {
    if (!Array.isArray(value) || value.length > 4096) return null;
    const ids = new Set<string>(), uids = new Set<number>(), result: Peers = [];
    for (const p of value) {
      if (!p || !text(p.id) || !uid(p.uid) || ids.has(p.id) || uids.has(p.uid)) return null;
      ids.add(p.id); uids.add(p.uid);
      if (p.id === self || p.uid === ownUid) { if (p.id !== self || p.uid !== ownUid) return null; continue; }
      result.push({ peerId: p.id, uid: p.uid });
    }
    return result;
  }
  function acceptGeneration(n: unknown): boolean {
    if (!counter(n) || n < generation) return false;
    if (n > generation) { generation = n; wait(); }
    return true;
  }
  return {
    welcome(flags: unknown, peerId: unknown) {
      socket++; clear(); generation = -1; lastChanged = -1; lastVersion = -1; current = null;
      enabled = Array.isArray(flags) && flags.includes('voice-agora');
      self = text(peerId) ? peerId : '';
      if (!enabled) { blockedMic = null; options.enterSfu(); return; }
      options.enterAgoraWait();
      if (blockedMic) { options.unavailable(blockedMic); return; }
      if (!self) { down(); return; }
      request(); retry();
    },
    offline() { socket++; clear(); if (enabled) options.unavailable(blockedMic ?? 'disconnected'); enabled = false; current = null; self = ''; },
    connectionDown(reason?: AgoraMicUnavailable) { if (enabled) down(reason); },
    refresh() { if (enabled) { request(); retry(); } },
    microphoneBlocked() { return !!blockedMic; },
    retryMicrophone() {
      if (!blockedMic) return false;
      blockedMic = null;
      if (enabled) { wait(); request(); retry(); }
      return true;
    },
    message(m: any) {
      if (!enabled || !m || typeof m !== 'object') return;
      // Keep the socket generation current but do not restart capture until the user retries.
      if (blockedMic) {
        if (m.t === 'agora_changed' && counter(m.generation) && m.generation >= generation && m.generation > lastChanged) {
          lastChanged = m.generation; generation = m.generation;
        }
        return;
      }
      if (m.t === 'agora_changed') {
        if (!counter(m.generation) || m.generation < generation || m.generation <= lastChanged) return;
        lastChanged = m.generation; generation = m.generation; wait(); request(); retry();
        return;
      }
      if (m.t === 'agora_unavailable') {
        if (!acceptGeneration(m.generation)) return;
        if (counter(m.version) && m.version < lastVersion) return;
        if (counter(m.version)) lastVersion = m.version;
        down(['unsupported', 'recover', 'reauth'].includes(m.reason) ? m.reason : 'unavailable'); return;
      }
      if (m.t === 'agora_space') {
        if (!acceptGeneration(m.generation)) return;
        if (!counter(m.version) || m.version < lastVersion) return;
        if (m.version === lastVersion && (!current || current.leaseId !== m.leaseId)) return;
        const list = peersOf(m.peers, m.uid);
        if (!text(m.appId) || !text(m.leaseId) || !text(m.epoch) || !text(m.channel) || !text(m.token) || !uid(m.uid)
          || !counter(m.expiresAt) || !counter(m.renewAt) || !counter(m.overlapUntil) || m.expiresAt <= Date.now()
          || m.renewAt >= m.expiresAt || !list || m.publish?.audio !== true || m.publish?.video !== false || m.publish?.data !== false) { down(); return; }
        if (current?.leaseId === m.leaseId && m.version === lastVersion) { retry(Math.max(250, m.renewAt - Date.now())); return; }
        if (current && current.leaseId === m.leaseId) {
          const old = current, conn = socket;
          if (old.stt !== (m.capture?.stt === true)) { down(); return; }
          if (old.epoch !== m.epoch || old.uid !== m.uid || old.appId !== m.appId || old.channel !== m.channel) { down(); return; }
          const same = list.length === old.peers.length && list.every((p) => old.peers.some((q) => q.uid === p.uid && q.peerId === p.peerId));
          if (!old.ready) {
            // No SDK session can renew yet. Keep an unchanged in-flight grant until it joins or expires.
            if (!same) { lastVersion = m.version; wait(); request(); retry(); }
            return;
          }
          lastVersion = old.version = m.version; old.peers = list;
          try { options.roster(old.leaseId, old.version, list.map((p) => p.uid), list); } catch { down(); return; }
          if (m.expiresAt <= Math.max(old.expiresAt, old.pendingExpiry)) return;
          const sequence = ++old.renewal, expiresAt = m.expiresAt, renewAt = m.renewAt; old.pendingExpiry = expiresAt;
          void options.renew(old.leaseId, m.token, expiresAt).then(() => {
            if (!enabled || socket !== conn || current !== old) return;
            old.expiresAt = Math.max(old.expiresAt, expiresAt);
            if (old.renewal === sequence) retry(Math.max(250, renewAt - Date.now()));
          }).catch(() => { if (enabled && socket === conn && current === old && old.renewal === sequence) down(); });
          return;
        }
        const token = m.token, leaseId = m.leaseId, epoch = m.epoch, renewAt = m.renewAt, expiresAt = m.expiresAt, g = generation, conn = socket;
        const lease = { leaseId, appId: m.appId, channel: m.channel, token, uid: m.uid, expiresAt: m.expiresAt,
          mayPublishAudio: true, authorizationVersion: m.version, allowedAudioUids: list.map((p) => p.uid) };
        lastVersion = m.version;
        const next: Current = { generation: g, version: m.version, epoch, leaseId, appId: m.appId, channel: m.channel,
          uid: m.uid, expiresAt, pendingExpiry: 0, peers: list, ready: false, renewal: 0, stt: m.capture?.stt === true };
        current = next; clear();
        // Microphone permission or SDK join may never settle; the lease is still finite during that wait.
        timer = setTimeout(() => { if (enabled && socket === conn && current === next) { wait(); request(); retry(); } },
          Math.min(2_147_483_647, Math.max(0, expiresAt - Date.now())));
        void options.grant({ lease, peers: list, capture: { stt: next.stt, secretary: false } }).then(() => {
          if (!enabled || socket !== conn || current !== next) return;
          next.ready = true;
          try { options.roster(next.leaseId, next.version, next.peers.map((p) => p.uid), next.peers); }
          catch { down(); return; }
          retry(Math.max(250, renewAt - Date.now()));
        }).catch(() => { if (enabled && socket === conn && current === next) down(); });
        return;
      }
      if (m.t === 'agora_roster') {
        const before = generation;
        if (!acceptGeneration(m.generation)) return;
        if (!current) { if (generation > before) { request(); retry(); } return; }
        if (m.epoch !== current.epoch || !counter(m.version) || m.version < lastVersion) return;
        const list = peersOf(m.peers, current.uid); if (!list) { down(); return; }
        const same = list.length === current.peers.length && list.every((p) => current!.peers.some((old) => old.uid === p.uid && old.peerId === p.peerId));
        if (m.version === lastVersion) {
          if (!same) down();
          return;
        }
        lastVersion = current.version = m.version; current.peers = list;
        // A roster change while joining must not leave a stale authorization in that in-flight connection.
        // Drop it synchronously and request a fresh grant; do not briefly open audio under the old roster.
        if (!current.ready) { if (!same) { wait(); request(); retry(); } return; }
        try { options.roster(current.leaseId, current.version, list.map((p) => p.uid), list); } catch { down(); }
      }
    },
  };
}
